// src/pages/auth/LoginPage.jsx

import { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { CheckCircle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Button, Input } from '../../components/ui';
import { api } from '../../services/api';

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { sendCode, verifyCode, isAuthenticated, user } = useAuth();

  const [step, setStep] = useState('email'); // 'email' | 'pin'
  const [email, setEmail] = useState('');
  const [pin, setPin] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Check for signup success message
  useEffect(() => {
    if (location.state?.signupSuccess && location.state?.message) {
      setSuccessMessage(location.state.message);
      // Clear the state so message doesn't persist on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated && user) {
      const aclOnboardingCompleted = user?.acl_care?.acl_onboarding_completed;
      
      if (!aclOnboardingCompleted) {
        navigate('/onboarding', { replace: true });
      } else {
        const returnTo = location.state?.returnTo || '/program';
        navigate(returnTo, { replace: true });
      }
    }
  }, [isAuthenticated, user, navigate, location]);

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');
    setLoading(true);

    // Audit: Login attempt (email submitted)
    api.createPublicAuditLog({
      event_type: 'USER_LOGIN',
      user_name: email,
      description: 'User initiated login by submitting email',
      outcome: 'attempt',
      additional_data: {
        step: 'email_submitted',
        email: email
      }
    });

    try {
      const result = await sendCode(email);
      if (result.success) {
        setStep('pin');
      } else {
        setError(result.error || 'Failed to send verification code. Please try again.');
      }
    } catch (err) {
      setError('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePinSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await verifyCode(email, pin);
      if (result.success) {
        // Audit: Login success (pin verified)
        // Note: Using public endpoint since auth token was just set
        api.createPublicAuditLog({
          event_type: 'USER_LOGIN',
          user_name: email,
          description: 'User successfully verified PIN and logged in',
          outcome: 'success',
          additional_data: {
            step: 'pin_verified',
            email: email,
            is_new_user: result.isNewUser || false
          }
        });
        // Navigation handled by useEffect above
      } else {
        // Audit: Login failure (invalid pin)
        api.createPublicAuditLog({
          event_type: 'USER_LOGIN',
          user_name: email,
          description: 'User entered invalid PIN',
          outcome: 'failure',
          additional_data: {
            step: 'pin_failed',
            email: email,
            reason: result.error || 'Invalid verification code'
          }
        });
        setError(result.error || 'Invalid verification code. Please try again.');
      }
    } catch (err) {
      // Audit: Login failure (error)
      api.createPublicAuditLog({
        event_type: 'USER_LOGIN',
        user_name: email,
        description: 'Login verification failed due to error',
        outcome: 'failure',
        additional_data: {
          step: 'pin_failed',
          email: email,
          reason: 'Verification error'
        }
      });
      setError('Verification failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setError('');
    setLoading(true);

    try {
      const result = await sendCode(email);
      if (result.success) {
        setError(''); // Clear any existing error
        // Could add a success toast here
      } else {
        setError(result.error || 'Failed to resend code.');
      }
    } catch (err) {
      setError('Failed to resend code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleBackToEmail = () => {
    setStep('email');
    setPin('');
    setError('');
  };

  const renderEmailStep = () => (
    <form onSubmit={handleEmailSubmit} className="space-y-6">
      {successMessage && (
        <div className="p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2">
          <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
          <p className="text-sm text-green-800">{successMessage}</p>
        </div>
      )}

      <Input
        label="Email address"
        type="email"
        autoComplete="email"
        required
        value={email}
        onChange={(e) => {
          setEmail(e.target.value);
          setSuccessMessage('');
        }}
        placeholder="Enter your email"
        error={error}
      />

      <Button
        type="submit"
        loading={loading}
        fullWidth
        disabled={!email || loading}
      >
        Send Verification Code
      </Button>

      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-gray-200" />
        </div>
        <div className="relative flex justify-center text-sm">
          <span className="px-2 bg-white text-gray-500">or</span>
        </div>
      </div>

      <Link to="/signup">
        <Button
          type="button"
          variant="outline"
          fullWidth
        >
          Create Account
        </Button>
      </Link>
    </form>
  );

  const renderPinStep = () => (
    <form onSubmit={handlePinSubmit} className="space-y-6">
      <div>
        <Input
          label="Verification Code"
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          required
          value={pin}
          onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
          placeholder="Enter 6-digit code"
          maxLength={6}
          hint={`We sent a code to ${email}`}
          error={error}
        />
      </div>

      <Button
        type="submit"
        loading={loading}
        fullWidth
        disabled={pin.length !== 6 || loading}
      >
        Verify Code
      </Button>

      <div className="flex flex-col space-y-2">
        <button
          type="button"
          onClick={handleResendCode}
          disabled={loading}
          className="text-sm text-primary hover:text-primary-600 disabled:opacity-50"
        >
          Resend code
        </button>
        
        <button
          type="button"
          onClick={handleBackToEmail}
          disabled={loading}
          className="text-sm text-gray-600 hover:text-gray-800 disabled:opacity-50"
        >
          Use a different email
        </button>
      </div>
    </form>
  );

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center">
      <div className="max-w-md w-full mx-auto px-4">
        <div className="bg-white py-8 px-6 shadow-md rounded-lg">
          {/* Logo and Title */}
          <div className="text-center mb-8">
            <div className="flex justify-center mb-4">
              <img 
                src="/images/logo.png" 
                alt="ACL Care" 
                className="h-16 w-auto"
              />
            </div>
            <h1 className="text-3xl font-bold text-gray-900">ACL Care Pro</h1>
            <p className="mt-2 text-gray-600">
              {step === 'email' 
                ? 'Sign in with your email below.'
                : 'Enter the verification code sent to your email.'
              }
            </p>
          </div>

          {/* Step Content */}
          {step === 'email' ? renderEmailStep() : renderPinStep()}
        </div>

        {/* Footer */}
        <p className="mt-6 text-center text-xs text-gray-500">
          By continuing, you agree to our Terms of Service and Privacy Policy.
        </p>
      </div>
    </div>
  );
}